import {useEffect, useMemo, useState} from "react";
import {
    Box,
    Button,
    Chip,
    CircularProgress,
    Divider,
    IconButton,
    Modal,
    Paper,
    Typography,
} from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TodayIcon from "@mui/icons-material/Today";
import {useTranslation} from "react-i18next";

import {useAlert} from "../context/AlertContext";
import {getMyWeeklyScheduleSummary} from "../api/api";
import {MyScheduleSlot, MyWeeklyScheduleResponse} from "../types";

const pad2 = (n: number) => String(n).padStart(2, "0");
const toISODate = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

const startOfWeekMonday = (date: Date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = day === 0 ? -6 : 1 - day;
    d.setDate(d.getDate() + diff);
    d.setHours(0, 0, 0, 0);
    return d;
};

const addDays = (date: Date, days: number) => {
    const d = new Date(date);
    d.setDate(d.getDate() + days);
    return d;
};

const formatRangeLabel = (weekStartISO: string, locale?: string) => {
    const ws = new Date(weekStartISO + "T00:00:00");
    const we = addDays(ws, 6);
    return `${ws.toLocaleDateString(locale)} – ${we.toLocaleDateString(locale)}`;
};

const formatDayHeader = (weekStartISO: string, dayOfWeek: number, locale?: string) => {
    const ws = new Date(weekStartISO + "T00:00:00");
    const d = addDays(ws, dayOfWeek - 1);
    return d.toLocaleDateString(locale, {day: "2-digit", month: "short"});
};

const formatMobileDayNumber = (weekStartISO: string, dayOfWeek: number, locale?: string) => {
    const ws = new Date(weekStartISO + "T00:00:00");
    const d = addDays(ws, dayOfWeek - 1);
    return d.toLocaleDateString(locale, {day: "2-digit"});
};

const formatMobileMonth = (weekStartISO: string, dayOfWeek: number, locale?: string) => {
    const ws = new Date(weekStartISO + "T00:00:00");
    const d = addDays(ws, dayOfWeek - 1);
    return d.toLocaleDateString(locale, {month: "short"});
};

const getDayISO = (weekStartISO: string, dayOfWeek: number) => {
    const ws = new Date(weekStartISO + "T00:00:00");
    return toISODate(addDays(ws, dayOfWeek - 1));
};

const normalizeTime = (t: string) => (t?.length >= 5 ? t.slice(0, 5) : t);

const dayLabels = [
    {dayOfWeek: 1, key: "mon"},
    {dayOfWeek: 2, key: "tue"},
    {dayOfWeek: 3, key: "wed"},
    {dayOfWeek: 4, key: "thu"},
    {dayOfWeek: 5, key: "fri"},
    {dayOfWeek: 6, key: "sat"},
    {dayOfWeek: 7, key: "sun"},
] as const;

const getDayKeyByNumber = (dayOfWeek: number) => {
    return dayLabels.find((day) => day.dayOfWeek === dayOfWeek)?.key ?? "mon";
};

const MIN_HOUR = 9;
const MAX_HOUR = 22;
const HOURS = Array.from({length: MAX_HOUR - MIN_HOUR + 1}, (_, i) => MIN_HOUR + i);

const toMinutes = (hhmm: string) => {
    const [hh, mm] = normalizeTime(hhmm).split(":").map(Number);
    return hh * 60 + (mm || 0);
};

const weekdayMap: Record<string, number> = {
    Mon: 1,
    Tue: 2,
    Wed: 3,
    Thu: 4,
    Fri: 5,
    Sat: 6,
    Sun: 7,
};

const getZonedNowParts = (timeZone: string, now: Date) => {
    const formatter = new Intl.DateTimeFormat("en-US", {
        timeZone,
        weekday: "short",
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hourCycle: "h23",
    });

    const parts = formatter.formatToParts(now);
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? "";

    const weekday = get("weekday");
    const year = get("year");
    const month = get("month");
    const day = get("day");
    const hour = Number(get("hour"));
    const minute = Number(get("minute"));

    return {
        dayOfWeek: weekdayMap[weekday] ?? 0,
        dateISO: `${year}-${month}-${day}`,
        minutesOfDay: hour * 60 + minute,
    };
};

const isSlotLiveNow = (slot: MyScheduleSlot, timeZone: string, now: Date) => {
    const zoned = getZonedNowParts(timeZone, now);

    if (zoned.dayOfWeek !== slot.dayOfWeek) return false;
    if (zoned.dateISO < slot.activeFrom || zoned.dateISO > slot.activeTo) return false;

    const startMin = toMinutes(slot.startTime);
    const endMin = toMinutes(slot.endTime);

    return zoned.minutesOfDay >= startMin && zoned.minutesOfDay < endMin;
};

const getDurationLabel = (slot: MyScheduleSlot, hourShort: string, minuteShort: string) => {
    const start = toMinutes(slot.startTime);
    const end = toMinutes(slot.endTime);
    const diff = Math.max(0, end - start);

    const hours = Math.floor(diff / 60);
    const minutes = diff % 60;

    if (hours && minutes) return `${hours}${hourShort} ${minutes}${minuteShort}`;
    if (hours) return `${hours}${hourShort}`;
    return `${minutes}${minuteShort}`;
};

type PositionedSlot = {
    slot: MyScheduleSlot;
    topPx: number;
    heightPx: number;
    lane: number;
    lanesCount: number;
};

const MySchedulePage = () => {
    const {t, i18n} = useTranslation();
    const {showAlert} = useAlert();

    const [weekStart, setWeekStart] = useState(() => toISODate(startOfWeekMonday(new Date())));
    const [loading, setLoading] = useState(false);
    const [weekly, setWeekly] = useState<MyWeeklyScheduleResponse | null>(null);
    const [detailsOpen, setDetailsOpen] = useState(false);
    const [selectedSlot, setSelectedSlot] = useState<MyScheduleSlot | null>(null);
    const [now, setNow] = useState(new Date());

    useEffect(() => {
        const id = window.setInterval(() => {
            setNow(new Date());
        }, 30000);

        return () => window.clearInterval(id);
    }, []);

    const openDetails = (slot: MyScheduleSlot) => {
        setSelectedSlot(slot);
        setDetailsOpen(true);
    };

    const closeDetails = () => {
        setDetailsOpen(false);
        setSelectedSlot(null);
    };

    const fetchWeekly = async (weekStartISO: string) => {
        try {
            setLoading(true);
            const res = await getMyWeeklyScheduleSummary(weekStartISO);
            setWeekly(res);
        } catch (e: any) {
            showAlert(e?.message ?? t("myschedule.errors.loadSchedule"), "error");
            setWeekly(null);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchWeekly(weekStart);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [weekStart]);

    const goToday = () => setWeekStart(toISODate(startOfWeekMonday(new Date())));

    const goPrev = () => {
        const ws = new Date(weekStart + "T00:00:00");
        setWeekStart(toISODate(addDays(ws, -7)));
    };

    const goNext = () => {
        const ws = new Date(weekStart + "T00:00:00");
        setWeekStart(toISODate(addDays(ws, 7)));
    };

    const PX_PER_MIN = 1.2;
    const dayStartMin = MIN_HOUR * 60;
    const dayEndMin = MAX_HOUR * 60;
    const dayTotalMin = dayEndMin - dayStartMin;
    const dayColumnHeight = dayTotalMin * PX_PER_MIN;

    const positionedByDay = useMemo(() => {
        const result = new Map<number, PositionedSlot[]>();

        const slots = weekly?.slots ?? [];

        for (const d of dayLabels) {
            const daySlots = slots
                .filter((s) => s.dayOfWeek === d.dayOfWeek)
                .slice()
                .sort((a, b) => {
                    const timeCompare = normalizeTime(a.startTime).localeCompare(normalizeTime(b.startTime));
                    if (timeCompare !== 0) return timeCompare;
                    return (a.streamName ?? "").localeCompare(b.streamName ?? "");
                });

            const lanesEnd: number[] = [];

            const tmp = daySlots
                .map((slot) => {
                    const startMin = Math.max(dayStartMin, toMinutes(slot.startTime));
                    const endMin = Math.min(dayEndMin, toMinutes(slot.endTime));

                    return {slot, startMin, endMin};
                })
                .filter((x) => x.endMin > x.startMin);

            const assigned = tmp.map((x) => {
                let lane = -1;

                for (let i = 0; i < lanesEnd.length; i++) {
                    if (lanesEnd[i] <= x.startMin) {
                        lane = i;
                        break;
                    }
                }

                if (lane === -1) {
                    lane = lanesEnd.length;
                    lanesEnd.push(x.endMin);
                } else {
                    lanesEnd[lane] = x.endMin;
                }

                return {...x, lane};
            });

            const lanesCountByIdx = assigned.map((a) => {
                let maxLane = a.lane;

                for (const b of assigned) {
                    const overlap = a.startMin < b.endMin && b.startMin < a.endMin;
                    if (overlap) maxLane = Math.max(maxLane, b.lane);
                }

                return maxLane + 1;
            });

            const positioned: PositionedSlot[] = assigned.map((a, idx) => {
                const topPx = (a.startMin - dayStartMin) * PX_PER_MIN;
                const heightPx = Math.max(28, (a.endMin - a.startMin) * PX_PER_MIN);

                return {
                    slot: a.slot,
                    topPx,
                    heightPx,
                    lane: a.lane,
                    lanesCount: lanesCountByIdx[idx],
                };
            });

            result.set(d.dayOfWeek, positioned);
        }

        return result;
    }, [weekly]);

    const mobileSlotsByDay = useMemo(() => {
        const result = new Map<number, MyScheduleSlot[]>();
        const slots = weekly?.slots ?? [];

        for (const d of dayLabels) {
            const daySlots = slots
                .filter((slot) => slot.dayOfWeek === d.dayOfWeek)
                .slice()
                .sort((a, b) => {
                    const timeCompare = normalizeTime(a.startTime).localeCompare(normalizeTime(b.startTime));
                    if (timeCompare !== 0) return timeCompare;
                    return (a.streamName ?? "").localeCompare(b.streamName ?? "");
                });

            result.set(d.dayOfWeek, daySlots);
        }

        return result;
    }, [weekly]);

    const modalTitle = selectedSlot?.streamName ?? t("myschedule.scheduleSlot");
    const scheduleTimeZone = weekly?.timezone || "Asia/Almaty";
    const currentScheduleDateISO = getZonedNowParts(scheduleTimeZone, now).dateISO;

    const totalSlots = weekly?.slots?.length ?? 0;
    const liveSlotsCount = weekly?.slots?.filter((slot) => isSlotLiveNow(slot, scheduleTimeZone, now)).length ?? 0;

    const isSelectedSlotLive = selectedSlot
        ? isSlotLiveNow(selectedSlot, scheduleTimeZone, now)
        : false;

    const durationLabel = selectedSlot
        ? getDurationLabel(
            selectedSlot,
            t("myschedule.hourShort"),
            t("myschedule.minuteShort")
        )
        : "—";

    return (
        <Box sx={{px: {xs: 1.5, md: 3}}}>
            <Paper
                elevation={0}
                sx={(theme) => ({
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 2,
                    overflow: "hidden",
                })}
            >
                <Box
                    sx={(theme) => ({
                        px: {xs: 1.5, md: 2},
                        py: 2,
                        display: "flex",
                        alignItems: {xs: "stretch", sm: "center"},
                        gap: 1,
                        flexDirection: {xs: "column", sm: "row"},
                        borderBottom: `1px solid ${theme.palette.divider}`,
                        backgroundColor: theme.palette.background.paper,
                    })}
                >
                    <Box sx={{display: "flex", flexDirection: "column", minWidth: 0}}>
                        <Typography sx={{fontWeight: 800}} noWrap>
                            {t("myschedule.title")}
                        </Typography>

                        <Typography variant="caption" color="text.secondary" noWrap>
                            {weekly?.timezone
                                ? `${t("myschedule.timezone")}: ${weekly.timezone}`
                                : `${t("myschedule.timezone")}: —`}
                            {"  •  "}
                            {weekly?.slots ? t("myschedule.slotsCount", {count: weekly.slots.length}) : "—"}
                        </Typography>
                    </Box>

                    <Box sx={{flexGrow: 1, display: {xs: "none", sm: "block"}}}/>

                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            width: {xs: "100%", sm: "auto"},
                        }}
                    >
                        <IconButton onClick={goPrev} size="small">
                            <ChevronLeftIcon/>
                        </IconButton>

                        <Button
                            onClick={goToday}
                            startIcon={<TodayIcon/>}
                            size="small"
                            variant="outlined"
                            sx={{
                                textTransform: "none",
                                flexGrow: {xs: 1, sm: 0},
                                justifyContent: "center",
                                minWidth: 0,
                            }}
                        >
                            <Typography variant="body2" noWrap>
                                {formatRangeLabel(weekStart, i18n.language)}
                            </Typography>
                        </Button>

                        <IconButton onClick={goNext} size="small">
                            <ChevronRightIcon/>
                        </IconButton>
                    </Box>
                </Box>

                {loading ? (
                    <Box sx={{display: "flex", justifyContent: "center", py: 6}}>
                        <CircularProgress/>
                    </Box>
                ) : (
                    <>
                        {/* Desktop calendar - unchanged for web */}
                        <Box sx={{display: {xs: "none", md: "block"}, width: "100%", overflowX: "auto"}}>
                            <Box
                                sx={(theme) => ({
                                    display: "grid",
                                    gridTemplateColumns: "88px repeat(7, minmax(180px, 1fr))",
                                    borderTop: `1px solid ${theme.palette.divider}`,
                                })}
                            >
                                <Box
                                    sx={(theme) => ({
                                        borderRight: `1px solid ${theme.palette.divider}`,
                                        backgroundColor: theme.palette.background.paper,
                                        position: "sticky",
                                        left: 0,
                                        zIndex: 2,
                                    })}
                                />

                                {dayLabels.map((d) => (
                                    <Box
                                        key={d.dayOfWeek}
                                        sx={(theme) => ({
                                            textAlign: "center",
                                            py: 1,
                                            borderRight: d.dayOfWeek !== 7 ? `1px solid ${theme.palette.divider}` : "none",
                                            backgroundColor: theme.palette.background.paper,
                                        })}
                                    >
                                        <Typography variant="body2" sx={{fontWeight: 800}} noWrap>
                                            {t(`myschedule.daysShort.${d.key}`)}
                                        </Typography>

                                        <Typography variant="caption" color="text.secondary" noWrap>
                                            {formatDayHeader(weekStart, d.dayOfWeek, i18n.language)}
                                        </Typography>
                                    </Box>
                                ))}

                                <Box
                                    sx={(theme) => ({
                                        borderRight: `1px solid ${theme.palette.divider}`,
                                        backgroundColor: theme.palette.background.paper,
                                        position: "sticky",
                                        left: 0,
                                        zIndex: 1,
                                    })}
                                >
                                    <Box sx={{height: dayColumnHeight, position: "relative", pt: 1, pb: 1, mb: 5}}>
                                        {HOURS.map((h, idx) => {
                                            const top = (h * 60.4 - dayStartMin) * PX_PER_MIN;

                                            return (
                                                <Box
                                                    key={h}
                                                    sx={{
                                                        position: "absolute",
                                                        top,
                                                        left: 0,
                                                        width: "100%",
                                                        display: "flex",
                                                        justifyContent: "center",
                                                        transform: idx === 0 ? "none" : "translateY(-50%)",
                                                        pointerEvents: "none",
                                                    }}
                                                >
                                                    <Typography
                                                        variant="caption"
                                                        color="text.secondary"
                                                        sx={{
                                                            fontWeight: 700,
                                                            lineHeight: 1,
                                                            px: 0.5,
                                                            backgroundColor: "background.paper",
                                                        }}
                                                    >
                                                        {pad2(h)}:00
                                                    </Typography>
                                                </Box>
                                            );
                                        })}
                                    </Box>
                                </Box>

                                {dayLabels.map((d) => {
                                    const items = positionedByDay.get(d.dayOfWeek) ?? [];

                                    return (
                                        <Box
                                            key={d.dayOfWeek}
                                            sx={(theme) => ({
                                                borderRight: d.dayOfWeek !== 7 ? `1px solid ${theme.palette.divider}` : "none",
                                                backgroundColor: theme.palette.background.paper,
                                                p: 1,
                                            })}
                                        >
                                            <Box sx={{height: dayColumnHeight, position: "relative"}}>
                                                {HOURS.map((h) => {
                                                    const top = (h * 60 - dayStartMin) * PX_PER_MIN;

                                                    return (
                                                        <Box
                                                            key={h}
                                                            sx={(theme) => ({
                                                                position: "absolute",
                                                                top,
                                                                left: 0,
                                                                right: 0,
                                                                borderTop: `1px solid ${theme.palette.divider}`,
                                                                opacity: 0.6,
                                                            })}
                                                        />
                                                    );
                                                })}

                                                {items.map((p) => {
                                                    const leftPct = (p.lane / p.lanesCount) * 100;
                                                    const widthPct = 100 / p.lanesCount;
                                                    const isLive = isSlotLiveNow(p.slot, scheduleTimeZone, now);

                                                    return (
                                                        <Box
                                                            key={p.slot.id}
                                                            onClick={() => openDetails(p.slot)}
                                                            sx={{
                                                                position: "absolute",
                                                                top: p.topPx,
                                                                left: `${leftPct}%`,
                                                                width: `${widthPct}%`,
                                                                height: p.heightPx,
                                                                pr: 0.5,
                                                                pl: 0.5,
                                                                py: 1.5,
                                                                cursor: "pointer",
                                                            }}
                                                        >
                                                            <Box
                                                                sx={(theme) => ({
                                                                    "@keyframes livePulse": {
                                                                        "0%": {
                                                                            opacity: 1,
                                                                            transform: "scale(1)",
                                                                            boxShadow: "0 0 0 0 rgba(244,67,54,0.45)",
                                                                        },
                                                                        "70%": {
                                                                            opacity: 0.75,
                                                                            transform: "scale(1.12)",
                                                                            boxShadow: "0 0 0 6px rgba(244,67,54,0)",
                                                                        },
                                                                        "100%": {
                                                                            opacity: 1,
                                                                            transform: "scale(1)",
                                                                            boxShadow: "0 0 0 0 rgba(244,67,54,0)",
                                                                        },
                                                                    },
                                                                    height: "100%",
                                                                    border: `1px solid ${theme.palette.divider}`,
                                                                    borderRadius: 1.5,
                                                                    px: 1,
                                                                    py: 0.75,
                                                                    backgroundColor: theme.palette.action.selected,
                                                                    overflow: "hidden",
                                                                    display: "flex",
                                                                    flexDirection: "column",
                                                                    gap: 0.25,
                                                                    transition: "background-color 120ms ease",
                                                                    "&:hover": {
                                                                        backgroundColor: theme.palette.action.hover,
                                                                    },
                                                                })}
                                                            >
                                                                {isLive ? (
                                                                    <Box
                                                                        sx={{
                                                                            display: "flex",
                                                                            alignItems: "center",
                                                                            gap: 0.75,
                                                                            mb: 0.25,
                                                                        }}
                                                                    >
                                                                        <Box
                                                                            sx={(theme) => ({
                                                                                width: 8,
                                                                                height: 8,
                                                                                borderRadius: "50%",
                                                                                backgroundColor: theme.palette.error.main,
                                                                                animation: "livePulse 1.2s infinite ease-in-out",
                                                                                flexShrink: 0,
                                                                            })}
                                                                        />

                                                                        <Typography
                                                                            variant="caption"
                                                                            sx={(theme) => ({
                                                                                color: theme.palette.error.main,
                                                                                fontWeight: 800,
                                                                                lineHeight: 1,
                                                                            })}
                                                                            noWrap
                                                                        >
                                                                            {t("myschedule.inProgress")}
                                                                        </Typography>
                                                                    </Box>
                                                                ) : null}

                                                                <Typography
                                                                    variant="body2"
                                                                    sx={{fontWeight: 600}}
                                                                    color="text.primary"
                                                                    noWrap
                                                                >
                                                                    {p.slot.streamName}
                                                                </Typography>

                                                                <Typography variant="caption" color="text.secondary" noWrap>
                                                                    {normalizeTime(p.slot.startTime)} – {normalizeTime(p.slot.endTime)}
                                                                </Typography>
                                                            </Box>
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        </Box>

                        {/* Mobile schedule */}
                        <Box
                            sx={{
                                display: {xs: "block", md: "none"},
                                p: 1.5,
                            }}
                        >
                            <Box
                                sx={(theme) => ({
                                    mb: 1.5,
                                    p: 1.5,
                                    borderRadius: 3,
                                    background:
                                        theme.palette.mode === "dark"
                                            ? "linear-gradient(135deg, rgba(30,41,59,0.95), rgba(15,23,42,0.95))"
                                            : "linear-gradient(135deg, rgba(248,250,252,1), rgba(241,245,249,1))",
                                    border: `1px solid ${theme.palette.divider}`,
                                })}
                            >
                                <Box sx={{display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1}}>
                                    <Box>
                                        <Typography sx={{fontWeight: 900, lineHeight: 1.2}}>
                                            {t("myschedule.weeklyAgenda")}
                                        </Typography>

                                        <Typography variant="caption" color="text.secondary">
                                            {t("myschedule.lessonsThisWeek", {count: totalSlots})}
                                        </Typography>
                                    </Box>

                                    {liveSlotsCount > 0 ? (
                                        <Chip
                                            size="small"
                                            label={t("myschedule.liveCount", {count: liveSlotsCount})}
                                            sx={(theme) => ({
                                                fontWeight: 900,
                                                color: theme.palette.error.main,
                                                backgroundColor:
                                                    theme.palette.mode === "dark"
                                                        ? "rgba(244,67,54,0.14)"
                                                        : "rgba(244,67,54,0.08)",
                                            })}
                                        />
                                    ) : (
                                        <Chip
                                            size="small"
                                            label={t("myschedule.noLiveLesson")}
                                            sx={(theme) => ({
                                                fontWeight: 700,
                                                backgroundColor: theme.palette.action.hover,
                                            })}
                                        />
                                    )}
                                </Box>

                                <Box
                                    sx={{
                                        mt: 1.5,
                                        display: "grid",
                                        gridTemplateColumns: "repeat(7, minmax(62px, 1fr))",
                                        gap: 0.75,
                                        overflowX: "auto",
                                        pb: 0.5,
                                    }}
                                >
                                    {dayLabels.map((day) => {
                                        const items = mobileSlotsByDay.get(day.dayOfWeek) ?? [];
                                        const dayISO = getDayISO(weekStart, day.dayOfWeek);
                                        const isToday = dayISO === currentScheduleDateISO;
                                        const hasLive = items.some((slot) => isSlotLiveNow(slot, scheduleTimeZone, now));

                                        return (
                                            <Box
                                                key={day.dayOfWeek}
                                                sx={(theme) => ({
                                                    minWidth: 62,
                                                    borderRadius: 2.5,
                                                    p: 1,
                                                    textAlign: "center",
                                                    border: `1px solid ${
                                                        isToday ? theme.palette.primary.main : theme.palette.divider
                                                    }`,
                                                    backgroundColor: isToday
                                                        ? theme.palette.action.selected
                                                        : theme.palette.background.paper,
                                                    position: "relative",
                                                })}
                                            >
                                                {hasLive ? (
                                                    <Box
                                                        sx={(theme) => ({
                                                            "@keyframes livePulse": {
                                                                "0%": {
                                                                    opacity: 1,
                                                                    transform: "scale(1)",
                                                                    boxShadow: "0 0 0 0 rgba(244,67,54,0.45)",
                                                                },
                                                                "70%": {
                                                                    opacity: 0.75,
                                                                    transform: "scale(1.12)",
                                                                    boxShadow: "0 0 0 6px rgba(244,67,54,0)",
                                                                },
                                                                "100%": {
                                                                    opacity: 1,
                                                                    transform: "scale(1)",
                                                                    boxShadow: "0 0 0 0 rgba(244,67,54,0)",
                                                                },
                                                            },
                                                            width: 8,
                                                            height: 8,
                                                            borderRadius: "50%",
                                                            backgroundColor: theme.palette.error.main,
                                                            animation: "livePulse 1.2s infinite ease-in-out",
                                                            position: "absolute",
                                                            top: 7,
                                                            right: 7,
                                                        })}
                                                    />
                                                ) : null}

                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{fontWeight: 800, display: "block"}}
                                                >
                                                    {t(`myschedule.daysShort.${day.key}`)}
                                                </Typography>

                                                <Typography sx={{fontWeight: 950, lineHeight: 1.2}}>
                                                    {formatMobileDayNumber(weekStart, day.dayOfWeek, i18n.language)}
                                                </Typography>

                                                <Typography
                                                    variant="caption"
                                                    color="text.secondary"
                                                    sx={{display: "block", lineHeight: 1}}
                                                >
                                                    {formatMobileMonth(weekStart, day.dayOfWeek, i18n.language)}
                                                </Typography>

                                                <Typography
                                                    variant="caption"
                                                    sx={(theme) => ({
                                                        mt: 0.7,
                                                        mx: "auto",
                                                        width: 24,
                                                        height: 20,
                                                        borderRadius: 999,
                                                        display: "flex",
                                                        alignItems: "center",
                                                        justifyContent: "center",
                                                        fontWeight: 900,
                                                        color: items.length ? theme.palette.text.primary : theme.palette.text.secondary,
                                                        backgroundColor: theme.palette.action.hover,
                                                    })}
                                                >
                                                    {items.length}
                                                </Typography>
                                            </Box>
                                        );
                                    })}
                                </Box>
                            </Box>

                            {dayLabels.map((day) => {
                                const items = mobileSlotsByDay.get(day.dayOfWeek) ?? [];
                                const dayISO = getDayISO(weekStart, day.dayOfWeek);
                                const isToday = dayISO === currentScheduleDateISO;

                                return (
                                    <Box key={day.dayOfWeek} sx={{mb: 2.25}}>
                                        <Box
                                            sx={{
                                                mb: 1,
                                                px: 0.5,
                                                display: "flex",
                                                alignItems: "center",
                                                justifyContent: "space-between",
                                                gap: 1,
                                            }}
                                        >
                                            <Box sx={{minWidth: 0}}>
                                                <Box sx={{display: "flex", alignItems: "center", gap: 0.75}}>
                                                    <Typography sx={{fontWeight: 950, lineHeight: 1.2}}>
                                                        {t(`myschedule.daysFull.${day.key}`)}
                                                    </Typography>

                                                    {isToday ? (
                                                        <Chip
                                                            size="small"
                                                            label={t("myschedule.today")}
                                                            sx={(theme) => ({
                                                                height: 20,
                                                                fontSize: 11,
                                                                fontWeight: 900,
                                                                color: theme.palette.primary.main,
                                                                backgroundColor: theme.palette.action.selected,
                                                            })}
                                                        />
                                                    ) : null}
                                                </Box>

                                                <Typography variant="caption" color="text.secondary">
                                                    {formatDayHeader(weekStart, day.dayOfWeek, i18n.language)}
                                                </Typography>
                                            </Box>

                                            <Typography
                                                variant="caption"
                                                color="text.secondary"
                                                sx={(theme) => ({
                                                    px: 1,
                                                    py: 0.4,
                                                    borderRadius: 999,
                                                    backgroundColor: theme.palette.action.hover,
                                                    fontWeight: 800,
                                                    flexShrink: 0,
                                                })}
                                            >
                                                {items.length} {items.length === 1 ? t("myschedule.slot") : t("myschedule.slots")}
                                            </Typography>
                                        </Box>

                                        {items.length === 0 ? (
                                            <Box
                                                sx={(theme) => ({
                                                    p: 2,
                                                    borderRadius: 3,
                                                    border: `1px dashed ${theme.palette.divider}`,
                                                    backgroundColor: theme.palette.action.hover,
                                                    textAlign: "center",
                                                })}
                                            >
                                                <Typography variant="body2" sx={{fontWeight: 700}} color="text.secondary">
                                                    {t("myschedule.noLessonsForDay")}
                                                </Typography>
                                            </Box>
                                        ) : (
                                            <Box
                                                sx={(theme) => ({
                                                    position: "relative",
                                                    pl: 2,
                                                    "&:before": {
                                                        content: '""',
                                                        position: "absolute",
                                                        top: 8,
                                                        bottom: 8,
                                                        left: 5,
                                                        width: "2px",
                                                        borderRadius: 999,
                                                        backgroundColor: theme.palette.divider,
                                                    },
                                                })}
                                            >
                                                {items.map((slot, index) => {
                                                    const isLive = isSlotLiveNow(slot, scheduleTimeZone, now);
                                                    const slotDuration = getDurationLabel(
                                                        slot,
                                                        t("myschedule.hourShort"),
                                                        t("myschedule.minuteShort")
                                                    );

                                                    return (
                                                        <Box
                                                            key={slot.id}
                                                            sx={{
                                                                position: "relative",
                                                                pb: index === items.length - 1 ? 0 : 1.25,
                                                            }}
                                                        >
                                                            <Box
                                                                sx={(theme) => ({
                                                                    "@keyframes livePulse": {
                                                                        "0%": {
                                                                            opacity: 1,
                                                                            transform: "scale(1)",
                                                                            boxShadow: "0 0 0 0 rgba(244,67,54,0.45)",
                                                                        },
                                                                        "70%": {
                                                                            opacity: 0.75,
                                                                            transform: "scale(1.12)",
                                                                            boxShadow: "0 0 0 6px rgba(244,67,54,0)",
                                                                        },
                                                                        "100%": {
                                                                            opacity: 1,
                                                                            transform: "scale(1)",
                                                                            boxShadow: "0 0 0 0 rgba(244,67,54,0)",
                                                                        },
                                                                    },
                                                                    position: "absolute",
                                                                    left: -14,
                                                                    top: 21,
                                                                    width: 10,
                                                                    height: 10,
                                                                    borderRadius: "50%",
                                                                    backgroundColor: isLive
                                                                        ? theme.palette.error.main
                                                                        : theme.palette.primary.main,
                                                                    border: `2px solid ${theme.palette.background.paper}`,
                                                                    animation: isLive ? "livePulse 1.2s infinite ease-in-out" : "none",
                                                                    zIndex: 1,
                                                                })}
                                                            />

                                                            <Box
                                                                onClick={() => openDetails(slot)}
                                                                sx={(theme) => ({
                                                                    p: 1.5,
                                                                    borderRadius: 3,
                                                                    border: `1px solid ${
                                                                        isLive ? theme.palette.error.main : theme.palette.divider
                                                                    }`,
                                                                    backgroundColor: theme.palette.background.paper,
                                                                    boxShadow:
                                                                        theme.palette.mode === "dark"
                                                                            ? "0 14px 32px rgba(0,0,0,0.32)"
                                                                            : "0 14px 32px rgba(15,23,42,0.07)",
                                                                    cursor: "pointer",
                                                                    overflow: "hidden",
                                                                    position: "relative",
                                                                    "&:before": {
                                                                        content: '""',
                                                                        position: "absolute",
                                                                        top: 0,
                                                                        bottom: 0,
                                                                        left: 0,
                                                                        width: 4,
                                                                        backgroundColor: isLive
                                                                            ? theme.palette.error.main
                                                                            : theme.palette.primary.main,
                                                                    },
                                                                })}
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        display: "flex",
                                                                        alignItems: "flex-start",
                                                                        justifyContent: "space-between",
                                                                        gap: 1.25,
                                                                    }}
                                                                >
                                                                    <Box sx={{minWidth: 0, flexGrow: 1}}>
                                                                        <Box
                                                                            sx={{
                                                                                display: "flex",
                                                                                alignItems: "center",
                                                                                gap: 0.75,
                                                                                flexWrap: "wrap",
                                                                                mb: 0.8,
                                                                            }}
                                                                        >
                                                                            {isLive ? (
                                                                                <Chip
                                                                                    size="small"
                                                                                    label={t("myschedule.inProgress")}
                                                                                    sx={(theme) => ({
                                                                                        height: 22,
                                                                                        fontSize: 11,
                                                                                        fontWeight: 950,
                                                                                        color: theme.palette.error.main,
                                                                                        backgroundColor:
                                                                                            theme.palette.mode === "dark"
                                                                                                ? "rgba(244,67,54,0.14)"
                                                                                                : "rgba(244,67,54,0.08)",
                                                                                    })}
                                                                                />
                                                                            ) : null}

                                                                            <Chip
                                                                                size="small"
                                                                                label={slot.lessonType ?? t("myschedule.lessonFallback")}
                                                                                sx={(theme) => ({
                                                                                    height: 22,
                                                                                    fontSize: 11,
                                                                                    fontWeight: 800,
                                                                                    backgroundColor: theme.palette.action.hover,
                                                                                })}
                                                                            />
                                                                        </Box>

                                                                        <Typography
                                                                            variant="subtitle2"
                                                                            sx={{
                                                                                fontWeight: 950,
                                                                                lineHeight: 1.35,
                                                                                wordBreak: "break-word",
                                                                            }}
                                                                        >
                                                                            {slot.streamName || "—"}
                                                                        </Typography>

                                                                        <Typography
                                                                            variant="caption"
                                                                            color="text.secondary"
                                                                            sx={{display: "block", mt: 0.5, fontWeight: 700}}
                                                                        >
                                                                            {t("myschedule.duration")}: {slotDuration}
                                                                        </Typography>
                                                                    </Box>

                                                                    <Box
                                                                        sx={(theme) => ({
                                                                            px: 1,
                                                                            py: 0.75,
                                                                            borderRadius: 2.5,
                                                                            backgroundColor: theme.palette.action.hover,
                                                                            flexShrink: 0,
                                                                            textAlign: "center",
                                                                            minWidth: 70,
                                                                        })}
                                                                    >
                                                                        <Typography
                                                                            variant="caption"
                                                                            sx={{fontWeight: 950, whiteSpace: "nowrap", lineHeight: 1}}
                                                                        >
                                                                            {normalizeTime(slot.startTime)}
                                                                        </Typography>

                                                                        <Typography
                                                                            variant="caption"
                                                                            color="text.secondary"
                                                                            sx={{display: "block", whiteSpace: "nowrap", lineHeight: 1.4}}
                                                                        >
                                                                            {normalizeTime(slot.endTime)}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>

                                                                {slot.notes?.trim() ? (
                                                                    <Box
                                                                        sx={(theme) => ({
                                                                            mt: 1.25,
                                                                            pt: 1.1,
                                                                            borderTop: `1px solid ${theme.palette.divider}`,
                                                                        })}
                                                                    >
                                                                        <Typography
                                                                            variant="body2"
                                                                            color="text.secondary"
                                                                            sx={{
                                                                                display: "-webkit-box",
                                                                                WebkitLineClamp: 2,
                                                                                WebkitBoxOrient: "vertical",
                                                                                overflow: "hidden",
                                                                            }}
                                                                        >
                                                                            {slot.notes}
                                                                        </Typography>
                                                                    </Box>
                                                                ) : null}
                                                            </Box>
                                                        </Box>
                                                    );
                                                })}
                                            </Box>
                                        )}
                                    </Box>
                                );
                            })}
                        </Box>
                    </>
                )}
            </Paper>

            <Modal open={detailsOpen} onClose={closeDetails}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        justifyContent: "center",
                        minHeight: "100vh",
                        pt: {xs: 4, sm: 8},
                        px: 2,
                    }}
                >
                    <Paper
                        elevation={0}
                        sx={(theme) => ({
                            width: {xs: "100%", sm: 560},
                            maxHeight: {xs: "calc(100vh - 64px)", sm: "none"},
                            borderRadius: 2,
                            border: `1px solid ${theme.palette.divider}`,
                            overflow: "hidden",
                        })}
                    >
                        <Box sx={{p: 2, borderBottom: (theme) => `1px solid ${theme.palette.divider}`}}>
                            <Typography sx={{fontWeight: 900}} noWrap>
                                {modalTitle}
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                p: 2,
                                display: "flex",
                                flexDirection: "column",
                                gap: 1.25,
                                overflowY: "auto",
                                maxHeight: {xs: "calc(100vh - 190px)", sm: "none"},
                            }}
                        >
                            {isSelectedSlotLive ? (
                                <Box
                                    sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        gap: 1,
                                        mb: 0.5,
                                    }}
                                >
                                    <Box
                                        sx={(theme) => ({
                                            "@keyframes livePulse": {
                                                "0%": {
                                                    opacity: 1,
                                                    transform: "scale(1)",
                                                    boxShadow: "0 0 0 0 rgba(244,67,54,0.45)",
                                                },
                                                "70%": {
                                                    opacity: 0.75,
                                                    transform: "scale(1.12)",
                                                    boxShadow: "0 0 0 6px rgba(244,67,54,0)",
                                                },
                                                "100%": {
                                                    opacity: 1,
                                                    transform: "scale(1)",
                                                    boxShadow: "0 0 0 0 rgba(244,67,54,0)",
                                                },
                                            },
                                            width: 10,
                                            height: 10,
                                            borderRadius: "50%",
                                            backgroundColor: theme.palette.error.main,
                                            animation: "livePulse 1.2s infinite ease-in-out",
                                            flexShrink: 0,
                                        })}
                                    />

                                    <Typography
                                        variant="body2"
                                        sx={(theme) => ({
                                            color: theme.palette.error.main,
                                            fontWeight: 900,
                                            lineHeight: 1,
                                        })}
                                    >
                                        {t("myschedule.inProgress")}
                                    </Typography>
                                </Box>
                            ) : null}

                            <Typography variant="body2" color="text.secondary">
                                <b>{t("myschedule.lessonType")}:</b> {selectedSlot?.lessonType ?? "—"}
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                <b>{t("myschedule.time")}:</b>{" "}
                                {selectedSlot
                                    ? `${normalizeTime(selectedSlot.startTime)} – ${normalizeTime(selectedSlot.endTime)}`
                                    : "—"}
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                <b>{t("myschedule.duration")}:</b> {durationLabel}
                            </Typography>

                            <Typography variant="body2" color="text.secondary">
                                <b>{t("myschedule.day")}:</b>{" "}
                                {selectedSlot?.dayOfWeek
                                    ? t(`myschedule.daysFull.${getDayKeyByNumber(selectedSlot.dayOfWeek)}`)
                                    : "—"}
                            </Typography>

                            <Divider/>

                            <Typography variant="body2" sx={{fontWeight: 800}}>
                                {t("myschedule.notes")}
                            </Typography>

                            <Typography variant="body2" color="text.secondary" sx={{whiteSpace: "pre-wrap"}}>
                                {selectedSlot?.notes?.trim() ? selectedSlot.notes : "—"}
                            </Typography>
                        </Box>

                        <Box
                            sx={{
                                p: 2,
                                borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                                display: "flex",
                                justifyContent: "flex-end",
                            }}
                        >
                            <Button onClick={closeDetails} variant="contained" sx={{textTransform: "none"}}>
                                {t("common.close")}
                            </Button>
                        </Box>
                    </Paper>
                </Box>
            </Modal>
        </Box>
    );
};

export default MySchedulePage;